import { TabContext, TabPanel, TimelineDot } from "@mui/lab";
import { Box, Grid2, Tab, Tabs, Typography } from "@mui/material";
import PrimaryIcon from "Components/Common/PrimaryIcon";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as CodeIcon } from "Assets/Icons/Notifications/CodeIcon.svg";
import { ReactComponent as ImproveIcon } from "Assets/Icons/Notifications/ImproveIcon.svg";
import { updateNotificationStatus } from "Slices/thunks";


const styles = (theme) => ({});

const NotificationList = ({ maxHeight = "auto" }) => {
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState("all");
  const [readNotifications, setReadNotifications] = useState(new Set());

  const containerRef = useRef(null);
  const itemRefs = useRef({});

  const { Notification, Translation } = useSelector((state) => ({
    Notification: state.Notification.notification,
    Translation: state.Translation.translation?.Notifications || {},
  }));

  const FilterNotification = React.useMemo(() => {
    return Notification?.filter((item) => selectedTab === "unread" ? !item.IsRead : true) || null;
  }, [Notification, selectedTab]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const data = JSON.parse(entry.target.getAttribute("data")) || {};

          if (entry.isIntersecting && !data.IsRead && !readNotifications.has(data.ID)) {
            // Call API to mark as read
            dispatch(updateNotificationStatus({NotificationID: data.ID}))

            // Update local state so it's not marked again
            setReadNotifications((prev) => new Set(prev).add(data.ID));
          }
        });
      },
      {
        root: containerRef.current,
        threshold: 0.5, // 50% of item should be visible
      }
    );

    // Observe all notification items
    Object.values(itemRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      observer.disconnect();
    };
  }, [FilterNotification, readNotifications]);

  return (
    <>
      <Grid2 sx={styles}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            type="primary"
            value={selectedTab}
            onChange={(e, val) => setSelectedTab(val)}
          >
            <Tab value={"all"} label={Translation?.AllNotifications} />
            <Tab value={"unread"} label={Translation?.Unread} />
          </Tabs>
        </Box>
        <Grid2
          className="notificationList"
          maxHeight={maxHeight}
          overflow={"auto"}
          ref={containerRef}
          sx={{
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {Array.isArray(FilterNotification) && FilterNotification.length ? FilterNotification.map((item, i) => {
            return (
              <Box
                key={i}
                data={JSON.stringify(item)}
                ref={(el) => (itemRefs.current[item.ID] = el)}
                display={"flex"}
                alignItems={"center"}
                gap="16px"
                padding={"10px 0px"}
                sx={(theme) => ({
                  "& svg": {
                    padding: "8px",
                    border: `1px solid ${theme.palette.border.grey}`,
                    borderRadius: "50%",
                  },
                  borderBottom: `1px solid ${theme.palette.border.grey}`,
                })}
              >
                <PrimaryIcon icon={CodeIcon} height="20px" width="20px" />
                <Box
                  width={"100%"}
                  display={"flex"}
                  flexDirection={"column"}
                  gap="8px"
                >
                  <Typography
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    fontWeight={500}
                  >
                    {item.Title}
                    {!item.IsRead && <TimelineDot color="primary" />}
                  </Typography>
                  <Typography color="secondary.light" variant="body2">
                    {item.Description}
                  </Typography>
                  <Typography
                    textAlign="right"
                    color="secondary.light"
                    variant="body2"
                  >
                    {new Date(item.NotificationDate).toLocaleString("default", {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </Typography>
                </Box>
              </Box> 
            )
          }) : <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100px"
          >
            <Typography variant="body1" color="secondary">
              {Translation?.NoNotificationFound}
            </Typography>
          </Box>
        }
        </Grid2>
      </Grid2>
    </>
  );
};

export default NotificationList;
